
import { useState, useRef } from 'react';
import Button from '../../components/base/Button';
import { caseStudies } from '../../mocks/portfolio';

interface ProcessImage {
  id: string;
  url: string;
  caption: string;
  description: string;
}

interface ProcessStep {
  title: string;
  description: string;
  insights: string[];
}

interface CaseStudy {
  id: number;
  title: string;
  category: string;
  description: string;
  image: string;
  tags: string[];
  duration: string;
  role: string;
  year: string;
  challenge: string;
  solution: string;
  results: string;
  processImages?: ProcessImage[];
  processSteps?: ProcessStep[];
}

export default function Admin() {
  const [studies, setStudies] = useState<CaseStudy[]>(caseStudies);
  const [editingStudy, setEditingStudy] = useState<CaseStudy | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    image: '',
    tags: '',
    duration: '',
    role: '',
    year: '',
    challenge: '',
    solution: '',
    results: ''
  });

  const categories = ['Mobile App Design', 'Web Design', 'Web & Mobile Design', 'Dashboard Design', 'Educational Technology', 'FinTech', 'IoT Interface'];

  const handleEdit = (study: CaseStudy) => {
    setEditingStudy(study);
    setFormData({
      title: study.title,
      category: study.category,
      description: study.description,
      image: study.image,
      tags: study.tags.join(', '),
      duration: study.duration,
      role: study.role,
      year: study.year,
      challenge: study.challenge || '',
      solution: study.solution || '',
      results: study.results || ''
    });
    setIsAddingNew(false);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingStudy(null);
    setFormData({
      title: '',
      category: categories[0],
      description: '',
      image: '',
      tags: '',
      duration: '',
      role: '',
      year: new Date().getFullYear().toString(),
      challenge: '',
      solution: '',
      results: ''
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          image: e.target?.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    if (isAddingNew) {
      const newStudy: CaseStudy = {
        id: Math.max(...studies.map(s => s.id)) + 1,
        title: formData.title,
        category: formData.category,
        description: formData.description,
        image: formData.image,
        tags: tagsArray,
        duration: formData.duration,
        role: formData.role,
        year: formData.year,
        challenge: formData.challenge,
        solution: formData.solution,
        results: formData.results,
        processImages: [],
        processSteps: []
      };
      setStudies([...studies, newStudy]);
    } else if (editingStudy) {
      const updatedStudy: CaseStudy = {
        ...editingStudy,
        title: formData.title,
        category: formData.category,
        description: formData.description,
        image: formData.image,
        tags: tagsArray,
        duration: formData.duration,
        role: formData.role,
        year: formData.year,
        challenge: formData.challenge,
        solution: formData.solution,
        results: formData.results
      };
      setStudies(studies.map(s => s.id === editingStudy.id ? updatedStudy : s));
    }

    setEditingStudy(null);
    setIsAddingNew(false);
    setFormData({
      title: '',
      category: '',
      description: '',
      image: '',
      tags: '',
      duration: '',
      role: '',
      year: '',
      challenge: '',
      solution: '',
      results: ''
    });
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this case study?')) {
      setStudies(studies.filter(s => s.id !== id));
    }
  };

  const handleCancel = () => {
    setEditingStudy(null);
    setIsAddingNew(false);
    setFormData({
      title: '',
      category: '',
      description: '',
      image: '',
      tags: '',
      duration: '',
      role: '',
      year: '',
      challenge: '',
      solution: '',
      results: ''
    });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white pt-16">
      {/* Header */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Portfolio Admin
              </h1>
              <p className="text-gray-400">Manage your case studies and portfolio content</p>
            </div>
            <Button variant="primary" onClick={handleAddNew}>
              <i className="ri-add-line mr-2"></i>
              Add New Case Study
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Case Studies List */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-6">Case Studies ({studies.length})</h2>
            
            {studies.map((study) => (
              <div key={study.id} className="bg-gray-900 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-purple-400 bg-purple-400/10 px-2 py-1 rounded">
                        {study.category}
                      </span>
                      <span className="text-sm text-gray-500">{study.year}</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 truncate">{study.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-3">{study.description}</p>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEdit(study)}
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(study.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Edit Form */}
          {(editingStudy || isAddingNew) && (
            <div className="bg-gray-900 rounded-xl p-6 sticky top-24">
              <h2 className="text-2xl font-bold mb-6">
                {isAddingNew ? 'Add New Case Study' : 'Edit Case Study'}
              </h2>

              <div className="space-y-6 max-h-[80vh] overflow-y-auto pr-2">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Project Image</label>
                  <div className="space-y-3">
                    {formData.image && (
                      <div className="aspect-video rounded-lg overflow-hidden">
                        <img 
                          src={formData.image} 
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex gap-3">
                      <Button 
                        variant="secondary" 
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <i className="ri-upload-line mr-2"></i>
                        Upload Image
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="Enter project title"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <div className="relative">
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 pr-8"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                    placeholder="Describe the project and its impact..."
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.description.length}/500
                  </div>
                </div>

                {/* Problem */}
                <div>
                  <label className="block text-sm font-medium mb-2">Problem</label>
                  <textarea
                    value={formData.challenge}
                    onChange={(e) => setFormData(prev => ({ ...prev, challenge: e.target.value }))}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                    placeholder="What problem were you solving?"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.challenge.length}/500
                  </div>
                </div>

                {/* Solution */}
                <div>
                  <label className="block text-sm font-medium mb-2">Solution</label>
                  <textarea
                    value={formData.solution}
                    onChange={(e) => setFormData(prev => ({ ...prev, solution: e.target.value }))}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                    placeholder="How did you solve the problem?"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.solution.length}/500
                  </div>
                </div>

                {/* Results */}
                <div>
                  <label className="block text-sm font-medium mb-2">Results</label>
                  <textarea
                    value={formData.results}
                    onChange={(e) => setFormData(prev => ({ ...prev, results: e.target.value }))}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 resize-none"
                    placeholder="What were the outcomes and impact?"
                  />
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.results.length}/500
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="UX Research, Mobile Design, User Flows"
                  />
                </div>

                {/* Role and Duration */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Role</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="Lead Product Designer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration</label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="4 months"
                    />
                  </div>
                </div>

                {/* Year */}
                <div>
                  <label className="block text-sm font-medium mb-2">Year</label>
                  <input
                    type="text"
                    value={formData.year}
                    onChange={(e) => setFormData(prev => ({ ...prev, year: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="2023"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button variant="primary" onClick={handleSave}>
                    <i className="ri-save-line mr-2"></i>
                    Save Changes
                  </Button>
                  <Button variant="ghost" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
